import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setAppTokenAsked,
  setAppTokenRetrieved,
  setCountry,
  setDomain,
  setSchool,
  setSliders,
  togglePreloader,
} from "../../store/actions";
import { withRouter } from "react-router-dom";
import {
  COUNTRY_AFRICAN,
  COUNTRY_FOREIGN,
  APP_TOKEN,
  SLIDERS,
} from "../../helpers/constants";
import {
  isNotEmptyArray,
  isUnauthenticatedRequest,
} from "../../helpers/utilities";
import { getSchools } from "../../services/schoolService";
import { getDomains } from "../../services/domainService";
import { getCountries } from "../../services/countryService";
import { getSettings } from "../../services/settingService";
import Carousels from "../../components/common/Carousels";
import Leading from "../../components/common/Leading";
import Section from "../../components/common/Section";
import Cta from "../../components/common/Cta";
import Searcher from "../../components/layout/Searcher";
import VisionList from "../../components/custom/home/VisionList";
import DomainList from "../../components/custom/home/DomainList";
import EstablishmentList from "../../components/custom/home/EstablishmentList";
import StudyList from "../../components/custom/home/StudyList";
import Community from "../../components/custom/home/Community";

const Home = ({ history }) => {
  const dispatch = useDispatch();
  const isPreloader = useSelector((state) => state.isPreloader);
  const sliders = useSelector((state) => state.sliders);
  const visions = useSelector((state) => state.visions);
  const schools = useSelector((state) => state.schools);
  const countries = useSelector((state) => state.countries);
  const domains = useSelector((state) => state.domains);
  const tokenRetrieved = useSelector((state) => state.tokenRetrieved);
  const tokenFailed = useSelector((state) => state.tokenFailed);
  const members = useSelector((state) => state.members);
  const [selectedCountries, setSelectedCountries] = React.useState([]);
  const [selectedContinent, setSelectedContinent] = React.useState(null);

  useEffect(() => {
    // Fetch data based on selected countries or continent
    const fetchData = async () => {
      dispatch(togglePreloader(true, "Chargement"));
      if (!localStorage.getItem(APP_TOKEN)) {
        dispatch(setAppTokenAsked(true));
      } else {
        await onGetSettings();
      }
    };
    fetchData();
  }, [dispatch, selectedCountries, selectedContinent]);

  useEffect(() => {
    if (tokenRetrieved && localStorage.getItem(APP_TOKEN)) {
      dispatch(setAppTokenRetrieved(false));
      onGetSettings();
    } else if (tokenFailed) {
      dispatch(togglePreloader(false, ""));
    }
  }, [tokenRetrieved, tokenFailed, dispatch]);

  const onGetSettings = async () => {
    try {
      const response = await getSettings();
      if (response && response.id) {
        const slidersData = isNotEmptyArray(response.banners)
          ? response.banners
          : SLIDERS;
        dispatch(setSliders(slidersData));
        onGetSchools();
      } else if (isUnauthenticatedRequest(response)) {
        dispatch(setAppTokenAsked(true));
      } else {
        console.error(response, "GET SETTINGS ERROR");
        onGetSchools();
      }
    } catch (error) {
      console.error(error, "GET SETTINGS ERROR");
      onGetSchools();
    }
  };

  const onGetSchools = async () => {
    try {
      let response;
      if (selectedCountries.length > 0) {
        response = await getSchools({ countries: selectedCountries });
      } else if (selectedContinent) {
        response = await getSchools({ continent: selectedContinent });
      } else {
        response = await getSchools();
      }

      if (response && response.data) {
        // Filter schools based on the selected continent or countries
        let filteredSchools = response.data;
        if (selectedContinent) {
          filteredSchools = filteredSchools.filter(
            (school) => school.continent === selectedContinent
          );
        } else if (selectedCountries.length > 0) {
          filteredSchools = filteredSchools.filter((school) =>
            selectedCountries.includes(school.country)
          );
        }

        dispatch(setSchool(filteredSchools));
        onGetDomains();
      } else if (isUnauthenticatedRequest(response)) {
        dispatch(setAppTokenAsked(true));
      } else {
        console.error(response, "GET SCHOOLS ERROR");
        onGetDomains();
      }
    } catch (error) {
      console.error(error, "GET SCHOOLS ERROR");
      onGetDomains();
    }
  };

  const onGetDomains = async () => {
    try {
      const response = await getDomains();
      if (response && response.data) {
        dispatch(setDomain(response.data));
        onGetCountries();
      } else if (isUnauthenticatedRequest(response)) {
        dispatch(setAppTokenAsked(true));
      } else {
        console.error(response, "GET DOMAINS ERROR");
        onGetCountries();
      }
    } catch (error) {
      console.error(error, "GET DOMAINS ERROR");
      onGetCountries();
    }
  };

  const onGetCountries = async () => {
    try {
      const response = await getCountries();
      if (response && response.data) {
        dispatch(setCountry(response.data));
        dispatch(togglePreloader(false, ""));
      } else if (isUnauthenticatedRequest(response)) {
        dispatch(setAppTokenAsked(true));
      } else {
        dispatch(togglePreloader(false, ""));
        console.error(response, "GET COUNTRIES ERROR");
      }
    } catch (error) {
      dispatch(togglePreloader(false, ""));
      console.error(error, "GET COUNTRIES ERROR");
    }
  };

  return (
    <React.Fragment>
      <div id="home-page" className={`${isPreloader ? "d-none" : "d-block"}`}>
        {/* Banner Section: Carousels, Leading, Searcher */}
        <Carousels
          items={sliders}
          infiniteLooped
          carouselsClasses="home-carousel"
          itemKey="home_carousel_item"
          withArrows={false}
        />
        <Leading
          title="Comment allez-vous changer le Monde ?"
          subtitle="Bien s'orienter et réussir : Ressources, conseils et opportunités"
        >
          <Searcher history={history} />
        </Leading>

        {/* Visions Section */}
        <Section
          subtitle="ACE Orientation vous accompagne dans vos projets d'études"
          sectionId="visions"
          sectionClasses="visions bg-blue py-5"
          textColor="white"
        >
          <VisionList items={visions} />
        </Section>

        {/* Establishments Section */}
        <Section
          subtitle="Découvrez les profils des derniers établissements référencés"
          sectionId="establishments"
          sectionClasses="establishments bg-white py-5"
          textColor="blue"
        >
          <EstablishmentList items={schools} />
        </Section>

        {/* Domains Section */}
        <Section
          title="Domaines d’études"
          subtitle="Explorez les filières et les formations dans votre domaine d’études"
          sectionId="domains"
          sectionClasses="domains bg-blue py-5"
          textColor="green"
        >
          <DomainList items={domains} />
        </Section>

        {/* CTA Section */}
        <Cta />

        {/* Studies In Africa Section */}
        <Section
          subtitle={COUNTRY_AFRICAN.label}
          sectionId="studies"
          sectionClasses="studies bg-white py-5"
        >
          <StudyList
            option={COUNTRY_AFRICAN.key}
            items={countries.filter((c) => c.continent === COUNTRY_AFRICAN.key)}
            selectedContinent={COUNTRY_AFRICAN.key}
            selectedCountries={selectedCountries}
          />
        </Section>

        {/* Studies Out Africa Section */}
        <Section
          subtitle={COUNTRY_FOREIGN.label}
          sectionId="studies"
          sectionClasses="studies bg-light py-5"
        >
          <StudyList
            option={COUNTRY_FOREIGN.key}
            items={countries.filter((c) => c.continent !== COUNTRY_AFRICAN.key)}
            selectedContinent={selectedContinent}
            selectedCountries={selectedCountries}
          />
        </Section>

        {/* Community Section */}
        <Section
          subtitle="Rejoignez gratuitement la Communauté ACE Orientation et accédez à toutes les ressources de la plate-forme !"
          sectionId="community"
          sectionClasses="community bg-blue py-5"
          textColor="white"
        >
          <Community items={members} />
        </Section>



      </div>
    </React.Fragment>
  );
};

export default withRouter(Home);
